import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

/**
 * Gaming-score WS surface:
 * - `SUBMIT_SCORING_METRICS` — host → server raw metrics (loose, passthrough).
 *
 * Scores are read back via REST (GET /realtime/machines/:machineId/gaming-score),
 * not pushed over WebSocket.
 */
export enum GamingScoreMessageType {
  SUBMIT_SCORING_METRICS = 'SUBMIT_SCORING_METRICS',
}

const componentScoreSchema = z.object({
  score: z.number(),
  maxScore: z.number().optional(),
  status: z.string(),
  meetsMinimum: z.boolean().optional(),
  details: z.record(z.any()).optional(),
});

const gamingScoreComponentsSchema = z.object({
  cpu: componentScoreSchema.optional(),
  gpu: componentScoreSchema.optional(),
  memory: componentScoreSchema.optional(),
  storage: componentScoreSchema.optional(),
  network: componentScoreSchema.optional(),
  videoEncoding: componentScoreSchema.optional(),
  environment: componentScoreSchema.optional(),
  trust: componentScoreSchema.optional(),
  osVersion: componentScoreSchema.optional(),
});

/** Loose `.passthrough()` — only render-critical fields are validated. */
export const gamingScoreFullDataSchema = z
  .object({
    overallScore: z.number(),
    overallStatus: z.string(),
    qualificationLevel: z.string(),
    recommendations: z.array(z.string()),
    componentScores: gamingScoreComponentsSchema.optional(),
    scoringConfigVersion: z.string().optional(),
    scoringSource: z.literal('ts_heuristic').optional(),
  })
  .passthrough();

export const submitScoringMetricsPayloadSchema = z.object({
  allMetrics: z.record(z.any()),
  networkBenchmark: z.record(z.any()).optional(),
  driverStatus: z.record(z.any()).optional(),
  // Latest PlaycastBenchmark snapshot attachment embedded by Playjector C++
  // (PlaycastBenchmarkAttachment). Validated loosely as a record — like
  // `driverStatus`/`networkBenchmark` — so producer-side evolution never
  // rejects the whole submission. The strong shape lives in the TS types
  // (PlaycastBenchmarkAttachment) consumed by the compute layer.
  playcastBenchmark: z.record(z.any()).optional(),
  machineIdTimestamp: z.number(),
  clientTimestamp: z.number(),
});

/**
 * `driverStatus` shape on the wire is mostly flat booleans (one per driver
 * surface) plus an optional `playcastDriverDetails` rich snapshot embedded by
 * Playjector C++ (`RemoteIO::buildDriverStatus`). We validate the known
 * boolean flags loosely and accept the nested details map as a generic
 * record. `.passthrough()` is critical: future C++ additions must NOT reject
 * the entire submission across the fleet — that incident is what this schema
 * was rewritten to prevent.
 */
const driverStatusSchema = z
  .object({
    inputCasterMouse: z.boolean().optional(),
    inputCasterKeyboard: z.boolean().optional(),
    filterDriverMouse: z.boolean().optional(),
    filterDriverKeyboard: z.boolean().optional(),
    shadowMouse: z.boolean().optional(),
    shadowKeyboard: z.boolean().optional(),
    touch: z.boolean().optional(),
    gameController: z.boolean().optional(),
    audioSource: z.boolean().optional(),
    displayManager: z.boolean().optional(),
    shadowDisplay: z.boolean().optional(),
    ShadowCam00: z.boolean().optional(),
    ShadowCam01: z.boolean().optional(),
    ShadowCam02: z.boolean().optional(),
    ShadowCam03: z.boolean().optional(),
    ShadowCam04: z.boolean().optional(),
    ShadowCam05: z.boolean().optional(),
    ShadowCam06: z.boolean().optional(),
    ShadowCam07: z.boolean().optional(),
    ShadowCam08: z.boolean().optional(),
    /** Dynamic pool slots beyond legacy hub routes (ShadowCam09+). Also accepted via `.passthrough()`. */
    ShadowCam09: z.boolean().optional(),
    /**
     * Per-driver runtime/shipped/reconciliation snapshot embedded by
     * Playjector. Schema-loose by design — the producer evolves independently
     * and the realtime-api just forwards this for diagnostics.
     */
    playcastDriverDetails: z.record(z.unknown()).optional(),
  })
  .passthrough();

/** Persisted record shape — returned by GET /gaming-score and stored in Redis. */
export const gamingScorePersistedDataSchema = z.object({
  machineId: z.string().min(1),
  gamingScore: z.number().finite(),
  components: gamingScoreComponentsSchema.optional(),
  fullData: gamingScoreFullDataSchema.optional(),
  calculatedAt: z.number(),
  lastUpdated: z.number(),
  driverStatus: driverStatusSchema.optional(),
});

export type GamingScorePersistedDataValidated = z.infer<typeof gamingScorePersistedDataSchema>;

const submitScoringMetricsSchema = baseMessageSchema
  .extend({ type: z.literal(GamingScoreMessageType.SUBMIT_SCORING_METRICS) })
  .merge(submitScoringMetricsPayloadSchema);

export const gamingScoreMessageUnion = submitScoringMetricsSchema;

export type SubmitScoringMetricsPayload = z.infer<typeof submitScoringMetricsPayloadSchema>;
export type GamingScoreMessage = z.infer<typeof gamingScoreMessageUnion>;

export const generators = {
  submitScoringMetrics: (payload: SubmitScoringMetricsPayload) => ({
    id: createMessageId(),
    type: GamingScoreMessageType.SUBMIT_SCORING_METRICS as const,
    timestamp: Date.now(),
    ...payload,
  }),
};

export const validators = {
  submitScoringMetrics: (message: unknown) => {
    const result = submitScoringMetricsSchema.safeParse(message);
    return result.success ? result.data : null;
  },
};
